<script setup>
import SquareDetailPc from "@variants/seer/square-detail/pc/index.vue";
import SquareDetailM from "@variants/seer/square-detail/m/index.vue";
import SquareDetailPcSkeleton from "@variants/seer/square-detail/pc/components/page-skeleton/index.vue";
import SquareDetailMSkeleton from "@variants/seer/square-detail/m/components/page-skeleton/index.vue";

const { $isMobile } = useNuxtApp();
const route = useRoute();
const squareId = computed(() => String(route.params.id || ""));
const { t, locale } = useI18n();

// 保持路由 setup 同步，让详情页自己的骨架从第一帧直接渲染。
const { data: squareInfo, pending } = useLazyAsyncData(
	() => `square-detail-${squareId.value}-${$isMobile ? "m" : "pc"}-${locale.value}`,
	async () => {
		const { getSquareDetail } = await import("~/api/api");
		const res = await getSquareDetail({
			square_id: squareId.value,
			v: "2",
		});
		return res.data;
	},
	{
		watch: [locale, squareId],
	}
);

definePageMeta({
	"m-showNavBar": false,
	"m-showFooter": true,
	"pc-showNavBar": true,
	"pc-showFooter": true,
	breadcrumbName: "square.detail.breadcrumb",
});

useHead({
	title: computed(() => squareInfo.value?.title || t("square.detail.pageTitle")),
	meta: [
		{
			name: "keywords",
			content: computed(
				() =>
					`${squareInfo.value?.title || t("square.detail.seo.defaultTitle")}, ${t("square.detail.seo.keywordsSuffix")}`
			),
		},
		{
			name: "description",
			content: computed(() =>
				squareInfo.value?.desc
					? `${squareInfo.value.desc} - ${t("square.detail.seo.descriptionSuffix")}`
					: t("square.detail.seo.descriptionSuffix")
			),
		},
		{
			property: "og:title",
			content: computed(() => squareInfo.value?.title || t("square.detail.pageTitle")),
		},
		{
			property: "og:description",
			content: computed(() =>
				squareInfo.value?.desc
					? `${squareInfo.value.desc} - ${t("square.detail.seo.descriptionShort")}`
					: t("square.detail.seo.descriptionShort")
			),
		},
		{
			property: "og:image",
			content: computed(() => squareInfo.value?.cover || "/logo.png"),
		},
	],
});

const isShortsMobile = computed(
	() => $isMobile && Number(squareInfo.value?.video_screen_mode) === 2
);

watch(
	isShortsMobile,
	isShorts => {
		if (!import.meta.client || !$isMobile) return;
		route.meta["m-showFooter"] = !isShorts;
	},
	{ immediate: true }
);

const isMobile = $isMobile;
</script>

<template>
	<main>
		<template v-if="pending">
			<SquareDetailMSkeleton v-if="isMobile" />
			<SquareDetailPcSkeleton v-else />
		</template>
		<SquareDetailPc v-else-if="!isMobile" :square-info="squareInfo" :square-id="squareId" />
		<SquareDetailM v-else :square-info="squareInfo" :square-id="squareId" />
	</main>
</template>
